// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import expressCheckout from "@mcmc/App/Handlers/Pages/Checkout/ExpressCheckout";
import { ExpressCheckoutResult } from "@mcmc/App/Services/ExpressCheckoutService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { CheckoutStepKey } from "@mcmc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmit.helper";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { NotifyBody } from "@mcmc/XDL/Components/Notify";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    disabled?: boolean;
    hasQuoteRequiredProducts?: boolean;
    extendedStyles?: ButtonPresentationProps;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const mapStateToProps = (state: McMcApplicationState) => {
    return {
        cart: getCurrentCartState(state).value,
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
    };
};

const mapDispatchToProps = {
    expressCheckout,
};

export const expressCheckoutButtonStyles: ButtonPresentationProps = {
    variant: "secondary",
    sizeVariant: "medium",
};

const ExpressCheckoutButton = ({
    history,
    toaster,
    fulfillmentMethod,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    expressCheckout,
    hasQuoteRequiredProducts,
    disabled,
    extendedStyles,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(expressCheckoutButtonStyles, extendedStyles));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);

    const showToasterMessage = (message: ReturnType<typeof siteMessage>, messageType: "danger" | "warning") => {
        toaster.addToast({
            body: (
                <NotifyBody>
                    <Typography className="title">{translate("Express Checkout")}</Typography>
                    {message}
                </NotifyBody>
            ),
            messageType,
        });
    };
    const expressCheckoutClickHandler = () => {
        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);
        setIsSubmitting(true);
        expressCheckout({
            cartId: "current",
            onSuccess: (result: ExpressCheckoutResult) => {
                let nextUrl = checkoutShippingPageUrl || "";
                if (result.addressesAreValid) {
                    nextUrl = checkoutReviewAndSubmitPageUrl || "";
                    if (result.defaultShippingServiceFound || fulfillmentMethod === FulfillmentMethod.PickUp) {
                        if (result.defaultPaymentMethodFound) {
                            nextUrl = `${nextUrl}?step=${CheckoutStepKey.review}&express=true`;
                        } else {
                            nextUrl = `${nextUrl}?step=${CheckoutStepKey.payment}&express=true`;
                            showToasterMessage(siteMessage("McMc_ExpressCheckout_DefaultPaymentNotFound"), "warning");
                        }
                    } else {
                        nextUrl = `${nextUrl}?step=${CheckoutStepKey.shipping}`;
                        showToasterMessage(siteMessage("McMc_ExpressCheckout_DefaultShipViaNotFound"), "warning");
                    }
                } else {
                    showToasterMessage(siteMessage("McMc_ExpressCheckout_DefaultAddressNotFound"), "warning");
                }
                history.push(nextUrl);
            },
            onError: (error: string) => {
                setIsSubmitting(false);
                showToasterMessage(error, "danger");
            },
        });
    };

    return (
        <>
            <Button
                {...styles}
                onClick={expressCheckoutClickHandler}
                disabled={disabled || isSubmitting}
                {...otherProps}
            >
                <EllipsisSpinner showSpinner={isSubmitting} label={translate("Express Checkout")} />
            </Button>
            <TwoButtonModal
                headlineText=""
                modalIsOpen={quoteRequiredModalIsOpen}
                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                onSubmit={expressCheckoutClickHandler}
                submitTestSelector="QuoteInCartMessageSubmit"
            ></TwoButtonModal>
        </>
    );
};

export default withToaster(connect(mapStateToProps, mapDispatchToProps)(withHistory(ExpressCheckoutButton)));
